import { useEffect, useCallback, useMemo, useRef, useState } from "react"
import { useLocation, useNavigate } from "react-router-dom"
import { api as portalApi } from "@/lib/api"
import type { UpcomingCall } from "@/lib/types"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import {
  Loader2,
  RefreshCw,
  Users,
  HeartPulse,
  FileCheck,
  PhoneCall,
  GitBranch,
  Wrench,
  ClipboardList,
  Power,
  BarChart3,
} from "lucide-react"
import { useOrganizationsStore } from "@/stores/organizationsStore"
import { usePatientsStore } from "@/stores/patientsStore"
import { useApcmOpsStore } from "../stores/apcmOpsStore"
import { PatientStateTable } from "./PatientStateTable"
import { PendingApprovals } from "./PendingApprovals"
import { DeploymentReadiness } from "./DeploymentReadiness"
import { PatientDetailDrawer } from "./PatientDetailDrawer"
import { ChartUpload } from "./ChartUpload"
import { EnrollmentStage } from "./pipeline/EnrollmentStage"
import { ConsentStage } from "./pipeline/ConsentStage"
import { CarePlanStage } from "./pipeline/CarePlanStage"
import { CheckInStage } from "./pipeline/CheckInStage"
import { BillingStage } from "./pipeline/BillingStage"
import { ActivityRail } from "./pipeline/ActivityRail"
import { PipelineGapsBanner } from "./pipeline/PipelineGapsBanner"
import { ClinicProgramTab } from "./program/ClinicProgramTab"
import { ProgramStatusBadge } from "./program/ProgramControls"
import { CallsDashboardTab } from "./dashboard/CallsDashboardTab"
import { bulkUpdateStates, getPipelineGaps, type PipelineGaps } from "../api/apcm-ops.api"
import { getProgram } from "../api/outreach-program.api"
import { useToast } from "@/components/ui/use-toast"
import { APCMState, APCM_OPS_TABS, type ApcmOpsTab } from "../types/apcm-ops.types"
import type { ProgramStatus } from "../types/outreach-program.types"

const scheduledCallTotal = (calls: UpcomingCall[]) =>
  calls.filter((c) => c.scheduled_call?.status === "ENABLED").length

const statCardConfig = [
  { label: "Total Patients", key: "total" as const, icon: Users },
  { label: "Consented", key: "consented" as const, icon: HeartPulse },
  { label: "Approved Plans", key: "approved" as const, icon: FileCheck },
  { label: "Check-ins This Month", key: "checkins" as const, icon: PhoneCall },
]

export function ApcmOpsPage() {
  const { toast } = useToast()
  const location = useLocation()
  const navigate = useNavigate()
  const orgId = useOrganizationsStore((s) => s.selectedOrganizationId)
  const { patients, fetchPatientsAdvanced, isLoading: patientsLoading } = usePatientsStore()

  const {
    lifecycleSummary,
    allPatientStates,
    pendingCarePlans,
    stats,
    stateFilter,
    searchQuery,
    selectedPatientIds,
    drawerPatientId,
    patientHistory,
    activeWorkflow,
    activeTab,
    isLoading,
    error,
    lastActionResult,
    fetchAll,
    openDrawer,
    closeDrawer,
    setStateFilter,
    setSearchQuery,
    setActiveTab,
    togglePatientSelection,
    selectAllVisible,
    clearSelection,
    clearActionResult,
  } = useApcmOpsStore()

  const intervalRef = useRef<NodeJS.Timeout | null>(null)
  const [chartUploadOpen, setChartUploadOpen] = useState(false)
  const [gaps, setGaps] = useState<PipelineGaps | null>(null)
  const [upcomingCalls, setUpcomingCalls] = useState<UpcomingCall[] | null>(null)
  // The clinic's outreach program: "Start N" is a manual run when it is disabled.
  const [program, setProgram] = useState<ProgramStatus | null>(null)

  // Never label one clinic's run with another clinic's status.
  useEffect(() => setProgram(null), [orgId])

  const loadProgram = useCallback(async () => {
    if (!orgId) return
    try {
      setProgram(await getProgram(orgId))
    } catch {
      setProgram(null)
    }
  }, [orgId])

  const loadGaps = useCallback(async () => {
    if (!orgId) return
    try {
      const g = await getPipelineGaps(orgId)
      setGaps(g)
    } catch {
      /* non-fatal — UI just won't show the banner */
    }
  }, [orgId])

  // Calls the scheduler will dial are a separate record from pipeline state:
  // a batch scheduled by hand leaves patients in CONSENT_NEEDED or
  // CARE_PLAN_UPLOADED while their call is already queued. The stages use
  // this to say so, and to move those patients to the *_SCHEDULED state the
  // post-call transitions expect.
  const loadUpcomingCalls = useCallback(async () => {
    if (!orgId) return
    try {
      setUpcomingCalls(await portalApi.getUpcomingCalls(orgId))
    } catch {
      setUpcomingCalls(null)
    }
  }, [orgId])

  const refresh = useCallback(() => {
    if (!orgId) return
    fetchAll(orgId)
    loadGaps()
    loadUpcomingCalls()
    loadProgram()
  }, [orgId, fetchAll, loadGaps, loadUpcomingCalls, loadProgram])

  const scheduledPatientIds = useMemo(() => {
    const ids = new Set<string>()
    for (const c of upcomingCalls ?? []) {
      if (c.scheduled_call?.status === "ENABLED" && c.patient?.id) ids.add(c.patient.id)
    }
    return ids
  }, [upcomingCalls])

  const unmarkedScheduled = useCallback(
    (states: string[]) =>
      allPatientStates
        .filter((s) => states.includes(s.current_state) && scheduledPatientIds.has(s.patient_id))
        .map((s) => s.patient_id),
    [allPatientStates, scheduledPatientIds],
  )
  const consentUnmarked = useMemo(
    () => unmarkedScheduled([APCMState.ENROLLMENT_CONFIRMED, APCMState.CONSENT_NEEDED]),
    [unmarkedScheduled],
  )
  const checkInUnmarked = useMemo(
    () => unmarkedScheduled([APCMState.CARE_PLAN_UPLOADED, APCMState.CHECK_IN_NEEDED]),
    [unmarkedScheduled],
  )

  const markScheduled = async (patientIds: string[], newState: APCMState) => {
    if (!orgId || patientIds.length === 0) return
    try {
      const result = await bulkUpdateStates({
        patient_ids: patientIds,
        organization_id: orgId,
        new_state: newState,
        agent_name: "apcm_ops_mark_scheduled",
      })
      toast({
        title: `Moved to ${newState}`,
        description: `${result.success} of ${result.total} patients updated.${result.failed > 0 ? ` ${result.failed} failed.` : ""}`,
        variant: result.failed > 0 ? "destructive" : "default",
      })
      refresh()
    } catch (err: any) {
      toast({
        title: "Update failed",
        description: err?.response?.data?.detail || err?.message || "An error occurred",
        variant: "destructive",
      })
    }
  }

  useEffect(() => {
    if (!orgId) return
    refresh()
    fetchPatientsAdvanced(orgId)
  }, [orgId, refresh, fetchPatientsAdvanced])

  useEffect(() => {
    intervalRef.current = setInterval(refresh, 30000)
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current)
    }
  }, [refresh])

  useEffect(() => {
    const params = new URLSearchParams(location.search)
    const tab = params.get("tab") as ApcmOpsTab | null
    if (tab && APCM_OPS_TABS.includes(tab) && tab !== activeTab) {
      setActiveTab(tab)
    } else if (!tab && activeTab !== "pipeline") {
      setActiveTab("pipeline")
    }
  }, [location.search, activeTab, setActiveTab])

  useEffect(() => {
    if (lastActionResult) {
      toast({
        title: lastActionResult.success ? "Success" : "Error",
        description: lastActionResult.message,
        variant: lastActionResult.success ? "default" : "destructive",
      })
      clearActionResult()
    }
  }, [lastActionResult, toast, clearActionResult])

  const handleBulkTransition = async (patientIds: string[], newState: string) => {
    if (!orgId) return
    try {
      const result = await bulkUpdateStates({
        patient_ids: patientIds,
        organization_id: orgId,
        new_state: newState,
        agent_name: "manual_bulk_transition",
      })
      toast({
        title: "Bulk transition complete",
        description: `${result.success} of ${result.total} patients moved. ${result.failed > 0 ? `${result.failed} failed.` : ""}`,
        variant: result.failed > 0 ? "destructive" : "default",
      })
      clearSelection()
      refresh()
    } catch (err: any) {
      toast({
        title: "Bulk transition failed",
        description: err?.message || "An error occurred",
        variant: "destructive",
      })
    }
  }

  const viewPatientsWithFilter = (states: string[]) => {
    setStateFilter(states.join(","))
    setActiveTab("patients")
    navigate("/apcm-ops?tab=patients", { replace: true })
  }

  const goToApprovals = () => {
    setActiveTab("approvals")
    navigate("/apcm-ops?tab=approvals", { replace: true })
  }

  const goToSetup = () => {
    setActiveTab("setup")
    navigate("/apcm-ops?tab=setup", { replace: true })
  }

  const drawerPatient = patients.find((p) => p.id === drawerPatientId)
  const stateCounts = lifecycleSummary?.state_counts || {}

  const statValues: Record<string, string | number> = {
    total: stats?.patients ?? lifecycleSummary?.total_patients ?? "-",
    consented:
      Math.max(stats?.apcm_consented_patients ?? 0, stats?.consented_by_state ?? 0) || "-",
    approved: Math.max(stats?.approved_plans_of_care ?? 0, stats?.approved_by_state ?? 0) || "-",
    // D4: distinct patients with a completed check-in this Central month (backend field).
    checkins: stats && typeof stats.checkins_this_month === "number" ? stats.checkins_this_month : "-",
  }

  if (!orgId) {
    return (
      <div className="flex items-center justify-center h-64 text-muted-foreground">
        Select an organization to view APCM operations
      </div>
    )
  }

  return (
    <div className="mx-auto max-w-7xl space-y-6 p-4 md:p-8">
      {/* Header */}
      <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <div className="min-w-0">
          <h1 className="text-2xl font-semibold tracking-tight">
            APCM Operations
          </h1>
          <p className="text-sm text-muted-foreground mt-0.5">
            Patient lifecycle from enrollment to monthly billing. Follow the pipeline top to bottom.
          </p>
          {program && (
            <p className="mt-1 flex items-center gap-1.5 text-xs text-muted-foreground">
              Clinic program <ProgramStatusBadge program={program} />
            </p>
          )}
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={refresh}
          disabled={isLoading}
          className="gap-2 self-start md:self-auto"
        >
          {isLoading ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
          ) : (
            <RefreshCw className="h-3.5 w-3.5" />
          )}
          Refresh
        </Button>
      </div>

      {/* Stat cards */}
      <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
        {statCardConfig.map((card) => {
          const Icon = card.icon
          const value = statValues[card.key]
          return (
            <div key={card.label} className="rounded-xl border bg-card p-4 md:p-5">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-sm font-medium text-muted-foreground">{card.label}</p>
                  <p className="mt-1 text-2xl font-semibold tabular-nums text-foreground">{value}</p>
                </div>
                <div className="rounded-lg bg-accent p-2 text-accent-foreground">
                  <Icon className="h-4 w-4" strokeWidth={1.75} />
                </div>
              </div>
            </div>
          )
        })}
      </div>

      {error && (
        <div className="rounded-xl border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm text-destructive">
          Some of this page failed to load, so counts may be incomplete: {error}
        </div>
      )}

      {/* Tabs */}
      <Tabs
        value={activeTab}
        onValueChange={(v) => {
          setActiveTab(v as ApcmOpsTab)
          navigate(v === "pipeline" ? "/apcm-ops" : `/apcm-ops?tab=${v}`, { replace: true })
        }}
      >
        <TabsList className="bg-muted/50 p-0.5">
          <TabsTrigger value="pipeline" className="text-xs gap-1.5">
            <GitBranch className="h-3 w-3" />
            Pipeline
          </TabsTrigger>
          <TabsTrigger value="program" className="text-xs gap-1.5">
            <Power className="h-3 w-3" />
            Clinic program
          </TabsTrigger>
          <TabsTrigger value="dashboard" className="text-xs gap-1.5">
            <BarChart3 className="h-3 w-3" />
            Calls dashboard
          </TabsTrigger>
          <TabsTrigger value="patients" className="text-xs gap-1.5">
            <Users className="h-3 w-3" />
            Patients
          </TabsTrigger>
          <TabsTrigger value="approvals" className="text-xs gap-1.5">
            <ClipboardList className="h-3 w-3" />
            Approvals
            {pendingCarePlans.length > 0 && (
              <span className="rounded-full bg-warning text-white px-1.5 py-px text-[10px] font-semibold leading-tight">
                {pendingCarePlans.length}
              </span>
            )}
          </TabsTrigger>
          <TabsTrigger value="setup" className="text-xs gap-1.5">
            <Wrench className="h-3 w-3" />
            Setup
          </TabsTrigger>
        </TabsList>

        <TabsContent value="pipeline" className="mt-5">
          {!lifecycleSummary && isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            </div>
          ) : !lifecycleSummary ? (
            <p className="text-center text-muted-foreground py-12">
              No lifecycle data available
            </p>
          ) : (
            <div className="space-y-4">
              <PipelineGapsBanner
                orgId={orgId}
                gaps={gaps}
                onRefresh={refresh}
                onViewPatients={viewPatientsWithFilter}
                onOpenSetup={goToSetup}
              />
              <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr),320px] gap-6">
                <div className="min-w-0">
                  <EnrollmentStage
                    orgId={orgId}
                    stateCounts={stateCounts}
                    onRefresh={refresh}
                  />
                  <ConsentStage
                    orgId={orgId}
                    stateCounts={stateCounts}
                    stuckScheduledCount={gaps?.stuck_per_state.CONSENT_SCHEDULED.count ?? 0}
                    alreadyScheduled={consentUnmarked.length}
                    onMarkScheduled={() =>
                      markScheduled(consentUnmarked, APCMState.CONSENT_SCHEDULED)
                    }
                    onViewPatients={viewPatientsWithFilter}
                    program={program}
                    onRefresh={refresh}
                  />
                  <CarePlanStage
                    orgId={orgId}
                    stateCounts={stateCounts}
                    pendingApprovalsCount={pendingCarePlans.length}
                    missingChartData={gaps?.consented_missing_chart_data.total ?? 0}
                    stuckGeneratingCount={gaps?.stuck_per_state.CARE_PLAN_GENERATING.count ?? 0}
                    stuckUploadingCount={gaps?.stuck_per_state.CARE_PLAN_UPLOADING.count ?? 0}
                    onRefresh={refresh}
                    onViewApprovals={goToApprovals}
                    onViewPatients={viewPatientsWithFilter}
                    onUploadCharts={() => setChartUploadOpen(true)}
                  />
                  <CheckInStage
                    orgId={orgId}
                    stateCounts={stateCounts}
                    stuckScheduledCount={gaps?.stuck_per_state.CHECK_IN_SCHEDULED.count ?? 0}
                    alreadyScheduled={checkInUnmarked.length}
                    onMarkScheduled={() =>
                      markScheduled(checkInUnmarked, APCMState.CHECK_IN_SCHEDULED)
                    }
                    onRefresh={refresh}
                    onViewPatients={viewPatientsWithFilter}
                    program={program}
                  />
                  <BillingStage
                    orgId={orgId}
                    stateCounts={stateCounts}
                    lastBackfillRanAt={gaps?.last_runs.billing_backfill_ran_at ?? null}
                    lastBackfillMonth={gaps?.last_runs.billing_backfill_latest_month ?? null}
                    onRefresh={refresh}
                  />
                </div>
                <aside className="lg:sticky lg:top-6 lg:self-start">
                  <ActivityRail
                    orgId={orgId}
                    stateCounts={stateCounts}
                    scheduledCallCount={upcomingCalls ? scheduledCallTotal(upcomingCalls) : null}
                    onViewPatients={viewPatientsWithFilter}
                    onOpenSetup={goToSetup}
                    onRefresh={refresh}
                  />
                </aside>
              </div>
            </div>
          )}
        </TabsContent>

        <TabsContent value="program" className="mt-5">
          {activeTab === "program" && (
            <ClinicProgramTab
              key={orgId}
              orgId={orgId}
              stateCounts={stateCounts}
              onProgramChange={setProgram}
              onRefresh={refresh}
            />
          )}
        </TabsContent>

        <TabsContent value="dashboard" className="mt-5">
          {activeTab === "dashboard" && <CallsDashboardTab key={orgId} orgId={orgId} />}
        </TabsContent>

        <TabsContent value="patients" className="mt-5">
          {patientsLoading && patients.length === 0 ? (
            <div className="flex items-center justify-center py-12 gap-2">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
              <span className="text-sm text-muted-foreground">Loading patient data...</span>
            </div>
          ) : (
            <PatientStateTable
              states={allPatientStates}
              patients={patients}
              stateFilter={stateFilter}
              searchQuery={searchQuery}
              selectedIds={selectedPatientIds}
              onStateFilterChange={setStateFilter}
              onSearchChange={setSearchQuery}
              onToggleSelect={togglePatientSelection}
              onSelectAll={selectAllVisible}
              onClearSelection={clearSelection}
              onRowClick={(id) => orgId && openDrawer(id, orgId)}
              onBulkTransition={handleBulkTransition}
            />
          )}
        </TabsContent>

        <TabsContent value="approvals" className="mt-5">
          <PendingApprovals
            carePlans={pendingCarePlans}
            orgId={orgId}
            onRefresh={refresh}
          />
        </TabsContent>

        <TabsContent value="setup" className="mt-5">
          <DeploymentReadiness orgId={orgId} onRefresh={refresh} />
        </TabsContent>
      </Tabs>

      {/* Chart upload dialog (triggered from Care Plan stage) */}
      <Dialog open={chartUploadOpen} onOpenChange={setChartUploadOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Upload patient charts</DialogTitle>
            <DialogDescription>
              Charts become PatientHealthSummary rows. Care plan generation silently skips patients
              without one, so uploading these unblocks generation.
            </DialogDescription>
          </DialogHeader>
          <ChartUpload
            orgId={orgId}
            onComplete={() => {
              setChartUploadOpen(false)
              refresh()
            }}
          />
        </DialogContent>
      </Dialog>

      {/* Patient Detail Drawer */}
      <PatientDetailDrawer
        open={!!drawerPatientId}
        onClose={closeDrawer}
        patientId={drawerPatientId}
        patient={drawerPatient}
        history={patientHistory}
        activeWorkflow={activeWorkflow}
        orgId={orgId}
        onRefresh={refresh}
      />
    </div>
  )
}
